import { Injectable } from '@angular/core'
import { HttpClient } from '@angular/common/http'

/**
 * Runtime configuration interface.
 * It contains all the properties that are loaded from the config.json file and
 * that could change depending on the env where the application is deployed.
 */
export interface RuntimeConfig {
   environment: string
   apiUrl: string
   dmsUrl: string
   pmpHomeUrl: string
   B4R_PROJECT_ID: number | null
   SITE_URL_B4R: string
   SHYPS_PROJECT_ID: number | null
   SPINE_PROJECT_ID: number | null
   SITE_URL_SPINE: string
   D4IF_PROJECT_ID: number | null
   SITE_URL_D4IF: string
   C4B_PROJECT_ID: number | null
   BINS_PROJECT_ID: number | null
   SITE_URL_BINS: string
}

/**
 * Service that provides the runtime configuration of the application.
 * It loads the configuration from a remote endpoint and provides it to the application.
 */
@Injectable({
   providedIn: 'root',
})
export class ConfigService {
   private readonly RUNTIME_CONFIG_PATH = 'assets/configs/properties.json'

   private config: RuntimeConfig = {
      environment: 'NONE',
      apiUrl: '',
      dmsUrl: '',
      pmpHomeUrl: '',
      B4R_PROJECT_ID: null,
      SITE_URL_B4R: '',
      SHYPS_PROJECT_ID: null,
      SPINE_PROJECT_ID: null,
      SITE_URL_SPINE: '',
      D4IF_PROJECT_ID: null,
      SITE_URL_D4IF: '',
      C4B_PROJECT_ID: null,
      BINS_PROJECT_ID: null,
      SITE_URL_BINS: '',
   }

   constructor(private httpClient: HttpClient) {}

   /**
    * Initializes the dynamic configuration from the config.json file.
    * @returns {Promise<RuntimeConfig>} The dynamic configuration.
    *
    * Workaround:
    * Fintanto che non viene correttamente sistemato il sistema di
    * cache-busting, e fintanto che tutti gli utenti che avevano acceduto
    * all'app in precedenza non puliscano i dati temporanei, si implementa
    * l'aggiunta di un url-param fittizio in coda alla chiamata.
    *
    * Il parametro (in questo caso 'ts') non viene veramente letto
    * o utilizzato dal proxy.
    * Ma il browser, vedendolo sempre differente, *dovrebbe* essere
    * forzato a NON utilizzare il config.json che ha in cache.
    */
   initRuntimeConfig(): Promise<RuntimeConfig> {
      return this.httpClient
         .get<RuntimeConfig>(`${this.RUNTIME_CONFIG_PATH}?ts=${Date.now()}`)
         .toPromise()
         .then((dc) => {
            if (!dc) {
               throw new Error('Runtime configuration not found')
            }
            this.config = dc
            console.log('Runtime Configuration loaded for env: ', this.environment)
            return dc
         })
   }

   /**
    * @returns {string} The current environment from the dynamic configuration. Expected values are NONE | LOCAL | DEV | TEST | PROD
    */
   get environment(): string {
      return this.config.environment
   }

   /**
    * Gets the API URL from the runtime configuration.
    *
    * @return {string} The API URL as specified in the runtime configuration.
    */
   get apiUrl(): string {
      return this.config.apiUrl
   }

   /**
    * Retrieves the Document Management System (DMS) URL from the configuration.
    * This is used primarily to correctly show the alfresco's <iframe>
    *
    * @return {string} The URL of the DMS as specified in the configuration.
    */
   get dmsUrl(): string {
      return this.config.dmsUrl
   }

   /**
    * Gets the PMP home URL from the runtime configuration.
    *
    * @return {string} The PMP home URL as specified in the runtime configuration.
    */
   get pmpHomeUrl(): string {
      return this.config.pmpHomeUrl
   }

   /**
    * Gets the B4R project ID from the runtime configuration.
    *
    * @return {number | null} The B4R project ID as specified in the runtime configuration.
    */
   get B4R_PROJECT_ID(): number | null {
      return this.config.B4R_PROJECT_ID
   }
   /**
    * Gets the B4R site URL from the runtime configuration.
    *
    * @return {string} The B4R site URL as specified in the runtime configuration.
    */
   get SITE_URL_B4R(): string {
      return this.config.SITE_URL_B4R
   }
   /**
    * Gets the SHYPS project ID from the runtime configuration.
    *
    * @return {number | null} The SHYPS project ID as specified in the runtime configuration.
    */
   get SHYPS_PROJECT_ID(): number | null {
      return this.config.SHYPS_PROJECT_ID
   }
   /**
    * Gets the SPINE project ID from the runtime configuration.
    *
    * @return {number | null} The SPINE project ID as specified in the runtime configuration.
    */
   get SPINE_PROJECT_ID(): number | null {
      return this.config.SPINE_PROJECT_ID
   }
   /**
    * Gets the SPINE site URL from the runtime configuration.
    *
    * @return {string} The SPINE site URL as specified in the runtime configuration.
    */
   get SITE_URL_SPINE(): string {
      return this.config.SITE_URL_SPINE
   }
   /**
    * Gets the D4IF project ID from the runtime configuration.
    *
    * @return {number | null} The D4IF project ID as specified in the runtime configuration.
    */
   get D4IF_PROJECT_ID(): number | null {
      return this.config.D4IF_PROJECT_ID
   }
   /**
    * Gets the D4IF site URL from the runtime configuration.
    *
    * @return {string} The D4IF site URL as specified in the runtime configuration.
    */
   get SITE_URL_D4IF(): string {
      return this.config.SITE_URL_D4IF
   }
   /**
    * Gets the C4B project ID from the runtime configuration.
    *
    * @return {number | null} The C4B project ID as specified in the runtime configuration.
    */
   get C4B_PROJECT_ID(): number | null {
      return this.config.C4B_PROJECT_ID
   }
   /**
    * Gets the BINS project ID from the runtime configuration.
    *
    * @return {number | null} The BINS project ID as specified in the runtime configuration.
    */
   get BINS_PROJECT_ID(): number | null {
      return this.config.BINS_PROJECT_ID
   }
   /**
    * Gets the BINS site URL from the runtime configuration.
    *
    * @return {string} The BINS site URL as specified in the runtime configuration.
    */
   get SITE_URL_BINS(): string {
      return this.config.SITE_URL_BINS
   }

   getSiteLogo(siteLogo:string): string {
      return this.config[siteLogo]
   }
}

